import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { HugeiconsIcon } from '@hugeicons/react'
import { studentApi } from '@/modules/students/students.service'
import { guardianApi } from '@/modules/guardians/guardians.service'
import {
  CheckmarkCircle02Icon,
  Cancel01Icon,
  SentIcon,
  Message01Icon,
  WhatsappIcon,
} from '@hugeicons/core-free-icons'
import {
  useWhatsappSessions,
  useSendWhatsappMessage,
  useCheckWhatsappNumber,
  useDefaultWhatsappSession,
} from '../whatsapp.hooks'
import { formatWaNumber } from '../whatsapp.utils'
import type { OpenWhatsappOptions } from '../whatsapp-composer'
import type { RecipientOption } from './recipient-combobox'
import { MessageRecipientField } from './message-recipient-field'
import { TemplatePicker } from '@/modules/templates/components/template-picker'
import {
  fillTemplate,
  useBranchName,
} from '@/modules/templates/template-variables'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'

export function WhatsappComposerDialog({
  open,
  onOpenChange,
  options,
  session,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  options: OpenWhatsappOptions | null
  session: number
}) {
  const { data: sessions = [] } = useWhatsappSessions(open)
  const { data: defaultSessionId } = useDefaultWhatsappSession()
  const branchName = useBranchName()
  const send = useSendWhatsappMessage()
  const check = useCheckWhatsappNumber()
  const queryClient = useQueryClient()
  const connected = sessions.filter((s) => s.status === 'connected')

  // Persist a verification for a stored student/guardian (their DB number), so
  // the result is saved and reflected in the lists. Silent: no toast.
  const persistVerification = useMutation({
    mutationFn: ({ type, id }: { type: 'student' | 'guardian'; id: string }) =>
      type === 'student'
        ? studentApi.verifyWhatsapp(id)
        : guardianApi.verifyWhatsapp(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] })
      queryClient.invalidateQueries({ queryKey: ['guardians'] })
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      queryClient.invalidateQueries({ queryKey: ['contact'] })
    },
  })

  const [recipient, setRecipient] = useState<RecipientOption | null>(null)
  const [phone, setPhone] = useState('')
  const [body, setBody] = useState('')
  const [sessionId, setSessionId] = useState('')
  // A recipient handed in from a profile is fixed (no "Cambiar").
  const [locked, setLocked] = useState(false)
  // null = not checked; true/false = whether the number is on WhatsApp.
  const [onWhatsapp, setOnWhatsapp] = useState<boolean | null>(null)

  // Re-initialize from options each time the composer is opened.
  useEffect(() => {
    if (options?.recipient) {
      setRecipient({
        id: options.recipient.id ?? '',
        type: options.recipient.type,
        name: options.recipient.name,
        firstName: options.recipient.firstName,
        phone: options.recipient.phone,
        email: null,
      })
      setPhone(options.recipient.phone)
      setLocked(true)
    } else {
      setRecipient(null)
      setPhone('')
      setLocked(false)
    }
    setBody(options?.body ?? '')
    setOnWhatsapp(options?.recipient?.verified ? true : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session])

  // Default the sending session to the branch's default (if connected),
  // otherwise the first connected one.
  useEffect(() => {
    const connectedSessions = sessions.filter((s) => s.status === 'connected')
    const preferred = defaultSessionId
      ? connectedSessions.find((s) => String(s.id) === defaultSessionId)
      : undefined
    const chosen = preferred ?? connectedSessions[0]
    setSessionId(chosen ? String(chosen.id) : '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, sessions.length, defaultSessionId])

  const digits = phone.replace(/\D/g, '')

  // Auto-verify against WhatsApp whenever the number or session changes. A
  // recipient handed in as already-verified is trusted and never re-checked.
  useEffect(() => {
    if (options?.recipient?.verified) {
      setOnWhatsapp(true)
      return
    }
    setOnWhatsapp(null)
    if (digits.length < 8 || !sessionId) return
    const timer = setTimeout(() => {
      // For a fixed student/guardian, persist the check (saves whatsappVerifiedAt
      // and refreshes the lists). Otherwise just check the number ephemerally.
      if (
        locked &&
        recipient?.id &&
        (recipient.type === 'student' || recipient.type === 'guardian')
      ) {
        persistVerification.mutate(
          { type: recipient.type, id: recipient.id },
          { onSuccess: (result) => setOnWhatsapp(result.verified) },
        )
      } else {
        check.mutate(
          { sessionId, phone: digits },
          { onSuccess: (result) => setOnWhatsapp(result?.exists ?? false) },
        )
      }
    }, 500)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits, sessionId])

  const clearRecipient = () => {
    setRecipient(null)
    setPhone('')
    setOnWhatsapp(null)
  }

  const checking = check.isPending || persistVerification.isPending

  // Only allow sending to a number confirmed on WhatsApp.
  const canSend =
    !!recipient &&
    digits.length >= 8 &&
    body.trim().length > 0 &&
    !!sessionId &&
    onWhatsapp === true &&
    !send.isPending

  const handleSend = () => {
    if (!canSend || !recipient) return
    send.mutate(
      {
        sessionId,
        message: {
          recipientType: recipient.type,
          recipientId: recipient.id || undefined,
          recipientName: recipient.name,
          toPhone: digits,
          body: body.trim(),
        },
      },
      { onSuccess: () => onOpenChange(false) },
    )
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full gap-0 data-[side=right]:sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Enviar mensaje por WhatsApp</SheetTitle>
          <SheetDescription>
            Escribe un mensaje directo a un contacto, estudiante o profesor.
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-6 py-4">
          <div className="flex flex-col gap-1.5">
            <Label className="flex items-center gap-1.5">
              <HugeiconsIcon
                icon={WhatsappIcon}
                className="size-4 text-muted-foreground"
              />
              Sesión de WhatsApp
            </Label>
            {connected.length === 0 ? (
              <p className="rounded-2xl border border-dashed px-3 py-2 text-xs text-muted-foreground">
                No hay sesiones conectadas. Conecta una en “Sesiones de
                WhatsApp” para poder enviar.
              </p>
            ) : (
              // A single connected session is shown as a disabled dropdown (visible
              // but not selectable); with 2+ it becomes a real picker.
              <Select
                value={sessionId}
                onValueChange={setSessionId}
                disabled={connected.length === 1}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecciona una sesión" />
                </SelectTrigger>
                <SelectContent>
                  {connected.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      <HugeiconsIcon
                        icon={WhatsappIcon}
                        className="size-4 shrink-0 text-emerald-600"
                      />
                      {s.name} · {formatWaNumber(s.phone_number)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <MessageRecipientField
            recipient={recipient}
            locked={locked}
            onClear={clearRecipient}
            onSelect={(next) => {
              setRecipient(next)
              setPhone(next.phone)
            }}
          >
            {digits ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs tabular-nums text-muted-foreground">
                  {formatWaNumber(phone)}
                </span>
                {checking ? (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Spinner className="size-3" />
                    Verificando…
                  </span>
                ) : onWhatsapp === true ? (
                  <span className="flex items-center gap-1 text-xs text-emerald-600">
                    <HugeiconsIcon
                      icon={CheckmarkCircle02Icon}
                      className="size-3.5"
                    />
                    Tiene WhatsApp
                  </span>
                ) : onWhatsapp === false ? (
                  <span className="flex items-center gap-1 text-xs text-rose-600">
                    <HugeiconsIcon icon={Cancel01Icon} className="size-3.5" />
                    No está en WhatsApp
                  </span>
                ) : null}
              </div>
            ) : (
              <Input
                inputMode="tel"
                placeholder="593999999999"
                aria-label="Teléfono del destinatario"
                className="mt-1 h-8"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
              />
            )}
          </MessageRecipientField>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="wa-body" className="flex items-center gap-1.5">
                <HugeiconsIcon
                  icon={Message01Icon}
                  className="size-4 text-muted-foreground"
                />
                Mensaje
              </Label>
              <TemplatePicker
                channel="whatsapp"
                onSelect={(template) =>
                  setBody(
                    fillTemplate(template.body, {
                      nombre: recipient?.firstName ?? recipient?.name,
                      nombre_completo: recipient?.name,
                      sucursal: branchName,
                    }),
                  )
                }
              />
            </div>
            <Textarea
              id="wa-body"
              placeholder="Escribe el mensaje…"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              maxLength={4096}
              className="min-h-44 resize-none"
            />
            <span className="self-end text-xs tabular-nums text-muted-foreground">
              {body.length} / 4096
            </span>
          </div>
        </div>

        <SheetFooter className="flex-row justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSend} disabled={!canSend}>
            <HugeiconsIcon icon={SentIcon} data-icon="inline-start" />
            {send.isPending ? 'Enviando…' : 'Enviar'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
